import { useEffect, useMemo, useState, useCallback } from 'react';
import { hasUserGridFilterState } from '../../utils';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  type RowData,
  type Row,
} from '@tanstack/react-table';
import { Paper, useMediaQuery, useTheme } from '@mui/material';
import type { DataGridProps } from './types';
import { useColumnDefinitions } from '../../hooks/useColumnDefinitions';
import { useDataGridState } from '../../hooks/useDataGridState';
import { useFilterOperators } from '../../hooks/useFilterOperators';
import { GridLoadingOverlay, LoadingState, PaginationControls } from './ui';
import { DataGridTable } from './table-structure';
import { FilterOperatorMenu } from './filter-inputs';
import { isUtilityGridColumn } from './utils';
import { useGridGrouping } from '../../hooks/useGridGrouping';
import type { GridDateTimeMode } from '../../utils';
import {
  DatePickerMaxDateContext,
  getEndOfToday,
} from '../../../../components/common/datePickerMaxDate';
import type { ConfigurationTables } from '../../hooks';
import type { LookupDataItem } from '../../types';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void;
    updateRowData: (rowIndex: number, patch: Record<string, unknown>) => void;
    nodeKey: string;
    parentId?: string;
    lookupMaps?: Map<string, Record<string, string>>;
    lookupOptionMaps?: Map<string, Record<string, string>>;
    rawLookupItems?: Map<string, LookupDataItem[]>;
    dropdownBaseFilter?: unknown;
    modifiedCells?: Set<string>;
    inlineEditable?: boolean;
    canEditSavedRows?: boolean;
    searchTerm?: string;
    validationErrors?: Map<string, string>;
    onRowNavigate?: (row: TData) => void;
    onRowActivate?: (row: TData) => void;
    dateTimeMode?: GridDateTimeMode;
    userRelationDays?: number | null;
    userRelationVacationDays?: string | null;
    canEditLockedWorkLogs?: boolean;
    currentUserRelationId?: number;
    configurationTables?: ConfigurationTables;
    dataOptionsMax?: boolean;
    wordWrap?: boolean;
  }
}

export const DataGrid = <T extends Record<string, unknown>>({
  focusRowId,
  data,
  columns: columnMetadata,
  lookupMaps,
  filterLookupMaps,
  rawLookupItems,
  dropdownBaseFilter,
  totalCount,
  serverSummary,
  page = 0,
  pageSize = 25,
  serverSide = false,
  filterResetTrigger,
  onDelete,
  onRestore,
  loading = false,
  overlayLoading = false,
  hidePaginationPanel = false,
  showPageSizeSelector = true,
  shrinkToRowNumbers = false,
  showLoadingPanel = true,
  wordWrap = false,
  nodeKey = '',
  parentId,
  onDataUpdate,
  onRowDataUpdate,
  modifiedCells,
  markedForDeletion,
  inlineEditable = false,
  canEditSavedRows = true,
  isDetailNav = false,
  onEdit,
  onRowActivate,
  maxHeight,
  searchTerm,
  showSearchPanel = true,
  showFilterPanel = true,
  validationErrors,
  columnVisibility,
  onColumnVisibilityChange,
  initialSorting,
  initialColumnFilters,
  columnFiltersSessionKey,
  initialColumnSizing,
  onSortChange,
  onColumnSizingChange,
  initialColumnOrder,
  onColumnOrderChange,
  onSortingChange,
  onFilterChange,
  onFilterSortActiveChange,
  onPageChange,
  onPageSizeChange,
  enableRowSelection = false,
  enableMultiSelect = false,
  rowSelection,
  onRowSelectionChange,
  onDeleteScreenshot,
  deletingScreenshotIds,
  onArchiveFinanceEmail,
  onRestoreFinanceEmail,
  onMarkFinanceEmailSpam,
  onUnmarkFinanceEmailSpam,
  dateTimeMode,
  userRelationDays,
  userRelationVacationDays,
  canEditLockedWorkLogs,
  currentUserRelationId,
  configurationTables,
  dataOptionsMax,
  onGroupFieldsChange,
  onExportStateChange,
}: DataGridProps<T>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Use custom hooks for column definitions
  const { columns, defaultColumn, columnMetadataMap } = useColumnDefinitions<T>({
    columnMetadata,
    lookupMaps: filterLookupMaps ?? lookupMaps,
    isMobile,
    enableRowSelection: enableMultiSelect,
    onDelete,
    onRestore,
    markedForDeletion,
    inlineEditable,
    isDetailNav,
    onDeleteScreenshot,
    deletingScreenshotIds,
    onArchiveFinanceEmail,
    onRestoreFinanceEmail,
    onMarkFinanceEmailSpam,
    onUnmarkFinanceEmailSpam,
  });

  // Use custom hook for grid state management
  const {
    sorting,
    setSorting,
    columnSizing,
    setColumnSizing,
    columnFilters,
    setColumnFilters,
    pagination,
    setPagination,
    headerHeight,
    headerRowRef,
    handlePageChange,
    handlePageSizeChange,
  } = useDataGridState({
    page,
    pageSize,
    serverSide: serverSide ?? false,
    onSortChange,
    data,
    columns,
    filterResetTrigger,
    initialSorting,
    initialColumnFilters,
    // Server-side filters are persisted by useGridPageLogic after operators
    // are enriched; client-side grids persist the raw table state here.
    columnFiltersSessionKey: serverSide ? undefined : columnFiltersSessionKey,
    initialColumnSizing,
    onColumnSizingChange,
    onSortingChange,
    onPageChange,
    onPageSizeChange,
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(initialColumnOrder ?? []);

  useEffect(() => {
    if (initialColumnOrder && initialColumnOrder.length > 0) {
      setColumnOrder(initialColumnOrder);
    }
  }, [initialColumnOrder]);

  // Use custom hook for filter operators
  const { filterOperators, setFilterOperators, filterMenuAnchor, setFilterMenuAnchor } =
    useFilterOperators({
      columnFilters,
      serverSide: serverSide ?? false,
      onFilterChange,
    });

  useEffect(() => {
    if (filterResetTrigger && filterResetTrigger > 0) {
      setFilterOperators({});
    }
  }, [filterResetTrigger, setFilterOperators]);

  useEffect(() => {
    onFilterSortActiveChange?.(hasUserGridFilterState({ sorting, columnFilters, searchTerm }));
  }, [sorting, columnFilters, searchTerm, onFilterSortActiveChange]);

  const tableMeta = useMemo(
    () => ({
      updateData: (rowIndex: number, columnId: string, value: unknown) => {
        if (onDataUpdate) {
          onDataUpdate(rowIndex, columnId, value);
        }
      },
      updateRowData: (rowIndex: number, patch: Record<string, unknown>) => {
        onRowDataUpdate?.(rowIndex, patch);
      },
      nodeKey: nodeKey || '',
      parentId,
      lookupMaps,
      lookupOptionMaps: filterLookupMaps,
      rawLookupItems,
      dropdownBaseFilter,
      modifiedCells: modifiedCells || new Set<string>(),
      inlineEditable: inlineEditable,
      canEditSavedRows,
      searchTerm: searchTerm || '',
      validationErrors,
      onRowNavigate: isDetailNav ? onEdit : undefined,
      onRowActivate,
      dateTimeMode,
      userRelationDays,
      userRelationVacationDays,
      canEditLockedWorkLogs,
      currentUserRelationId,
      configurationTables,
      dataOptionsMax,
      wordWrap,
    }),
    [
      onDataUpdate,
      onRowDataUpdate,
      nodeKey,
      parentId,
      lookupMaps,
      filterLookupMaps,
      rawLookupItems,
      dropdownBaseFilter,
      modifiedCells,
      inlineEditable,
      canEditSavedRows,
      searchTerm,
      validationErrors,
      isDetailNav,
      onEdit,
      onRowActivate,
      dateTimeMode,
      userRelationDays,
      userRelationVacationDays,
      canEditLockedWorkLogs,
      currentUserRelationId,
      configurationTables,
      dataOptionsMax,
      wordWrap,
    ]
  );

  const globalFilterFn = useMemo(
    () =>
      (row: Row<T>, columnId: string, filterValue: string): boolean => {
        if (String(row.original.Id ?? '').startsWith('temp_')) {
          return true;
        }
        if (isUtilityGridColumn(columnId)) {
          return false;
        }
        const rawValue = String(row.getValue(columnId) ?? '');
        const filterStr = String(filterValue).toLowerCase();
        const colMeta = columnMetadataMap.get(columnId);
        const lookupKey = colMeta?.dataField || columnId;
        const colLookupMap = lookupMaps?.get(lookupKey);
        if (colLookupMap) {
          const displayValue = colLookupMap[rawValue] ?? rawValue;
          return displayValue.toLowerCase().includes(filterStr);
        }
        return rawValue.toLowerCase().includes(filterStr);
      },
    [lookupMaps, columnMetadataMap]
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    defaultColumn,
    state: {
      sorting,
      columnSizing,
      columnFilters,
      columnVisibility,
      columnOrder,
      pagination,
      // Pass undefined (not empty string) when there is no search term so TanStack Table
      // skips globalFilterFn entirely (avoids iterating every row×column on each render).
      globalFilter: showSearchPanel ? searchTerm || undefined : undefined,
      ...(enableRowSelection && rowSelection != null && { rowSelection }),
    },
    getRowId: (row) => String((row as Record<string, unknown>).Id ?? ''),
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: onColumnVisibilityChange
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(columnVisibility ?? {}) : updater;
          onColumnVisibilityChange(next);
        }
      : undefined,
    onColumnOrderChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnOrder) : updater;
      setColumnOrder(next);
      onColumnOrderChange?.(next);
    },
    onRowSelectionChange: onRowSelectionChange
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(rowSelection ?? {}) : updater;
          onRowSelectionChange(next);
        }
      : undefined,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn,
    manualPagination: serverSide ?? false,
    pageCount:
      (serverSide ?? false) && pageSize && pageSize > 0
        ? Math.ceil((totalCount ?? 0) / pageSize)
        : undefined,
    // When data comes from the server (already sorted/filtered), disable expensive
    // client-side row processing to avoid iterating thousands of rows per render.
    manualSorting: serverSide ?? false,
    manualFiltering: serverSide ?? false,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    enableRowSelection,
    meta: tableMeta,
  });

  const {
    isGrouped,
    groupedRows,
    groupFields,
    groupCaptions,
    collapsedGroups,
    toggleGroup,
    reorderGroup,
    addGroupField,
    removeGroupField,
  } = useGridGrouping(
    table.getRowModel().rows,
    columnMetadata,
    onGroupFieldsChange,
    lookupMaps ?? filterLookupMaps
  );

  const handleColumnReorder = useCallback(
    (draggedId: string, targetId: string) => {
      setColumnOrder((prev) => {
        const leafColumnIds = table.getAllLeafColumns().map((c) => c.id);
        const base = prev.length > 0 ? prev : leafColumnIds;
        const next = [...base];
        const from = next.indexOf(draggedId);
        const to = next.indexOf(targetId);
        if (from === -1 || to === -1 || from === to) return prev;
        next.splice(from, 1);
        next.splice(to, 0, draggedId);
        onColumnOrderChange?.(next);
        return next;
      });
    },
    [table, onColumnOrderChange]
  );

  useEffect(() => {
    if (!onExportStateChange) return;
    const rows = (serverSide ? table.getSortedRowModel() : table.getFilteredRowModel()).rows.map(
      (row) => row.original
    );
    onExportStateChange(
      rows,
      table.getVisibleLeafColumns().map((column) => column.id),
      hasUserGridFilterState({ sorting, columnFilters, searchTerm })
    );
  }, [
    onExportStateChange,
    serverSide,
    table,
    sorting,
    columnFilters,
    searchTerm,
    data,
    columnVisibility,
    columnOrder,
  ]);

  if (loading) {
    return <LoadingState />;
  }

  const displayRowCount = serverSide ? (totalCount ?? 0) : table.getFilteredRowModel().rows.length;
  const displayPage = table.getState().pagination.pageIndex;
  const displayPageSize = table.getState().pagination.pageSize;
  const gridMaxDate = dataOptionsMax === true ? getEndOfToday() : null;

  return (
    <DatePickerMaxDateContext.Provider value={gridMaxDate}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          ...(shrinkToRowNumbers
            ? {
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'visible',
              }
            : {
                height: maxHeight ?? '100%',
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
              }),
        }}
      >
        <GridLoadingOverlay visible={showLoadingPanel && overlayLoading} />
        <DataGridTable
          focusRowId={focusRowId}
          table={table}
          columnMetadataMap={columnMetadataMap}
          filterOperators={filterOperators}
          setFilterMenuAnchor={setFilterMenuAnchor}
          lookupMaps={lookupMaps}
          filterLookupMaps={filterLookupMaps}
          rawLookupItems={rawLookupItems}
          manualFiltering={serverSide ?? false}
          showFilterPanel={showFilterPanel}
          shrinkToRowNumbers={shrinkToRowNumbers}
          wordWrap={wordWrap}
          headerHeight={headerHeight}
          headerRowRef={headerRowRef}
          modifiedCells={modifiedCells}
          serverSummary={serverSummary}
          markedForDeletion={markedForDeletion}
          onColumnReorder={handleColumnReorder}
          isGrouped={isGrouped}
          groupedRows={groupedRows}
          collapsedGroups={collapsedGroups}
          onToggleGroup={toggleGroup}
          groupFields={groupFields}
          groupCaptions={groupCaptions}
          onGroupReorder={reorderGroup}
          onGroupRemove={removeGroupField}
          onGroupAdd={addGroupField}
        />

        <FilterOperatorMenu
          filterMenuAnchor={filterMenuAnchor}
          setFilterMenuAnchor={setFilterMenuAnchor}
          setFilterOperators={setFilterOperators}
          columnMetadataMap={columnMetadataMap}
          table={table}
        />

        {!hidePaginationPanel && (
          <PaginationControls
            displayRowCount={displayRowCount}
            displayPage={displayPage}
            displayPageSize={displayPageSize}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            showPageSizeSelector={showPageSizeSelector}
          />
        )}
      </Paper>
    </DatePickerMaxDateContext.Provider>
  );
};

export default DataGrid;
